// src/accounts/pages/AccountHistory.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

export default function AccountHistory({ history = [], onClear }) {
  const items = Array.isArray(history) ? history : [];

  const formatTime = (ts) => {
    if (!ts) return "";
    const dt = new Date(ts);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ If activity.details is JSON string, try to parse (optional)
  const parseDetails = (d) => {
    if (!d) return null;
    if (typeof d === "object") return d;
    if (typeof d !== "string") return null;
    try {
      return JSON.parse(d);
    } catch {
      return null;
    }
  };

  // ✅ Nice label for common actions
  const prettyTitle = (h) => {
    // Prefer explicit action
    const a = String(h?.action || "").trim();
    if (a) return a;

    // fallback for old search-history format (animal/result_text)
    if (h?.result_text) return `Search: ${h.result_text}`;
    if (h?.animal) return `Search: ${h.animal}`;
    return "Activity";
  };

  const normalize = useMemo(() => {
    return items.map((h) => {
      const detailsObj = parseDetails(h?.details);

      const title = prettyTitle(h);
      const meta = h?.created_at ? formatTime(h.created_at) : h?.date || "";
      const status = (h?.status || "").toLowerCase(); // success|failed|info
      const url = h?.url || h?.result_url || "";
      const type = h?.type || "";

      // ✅ show best “result” text
      const result =
        h?.result_text ||
        detailsObj?.result ||
        detailsObj?.plan ||
        detailsObj?.email ||
        h?.animal ||
        "";

      return {
        title,
        meta,
        status,
        url,
        type,
        result,
        raw: h,
      };
    });
  }, [items]);

  return (
    <section className="acctPage">
      <h2 className="acctPageTitle">Account Activities</h2>

      {normalize.length === 0 && <p className="acctMuted">No Results Yet.</p>}

      {normalize.map((h, i) => (
        <div key={h?.raw?.id || i} className="acctRow">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontWeight: 800 }}>
              {h.url ? (
                <Link to={h.url} style={{ textDecoration: "underline", color: "inherit" }}>
                  {h.title}
                </Link>
              ) : (
                h.title
              )}
            </div>

            {h.status ? (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #e6e6e6",
                  opacity: 0.85,
                }}
                title={h.status}
              >
                {h.status}
              </span>
            ) : null}

            {h.type ? (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #e6e6e6",
                  opacity: 0.55,
                }}
                title={h.type}
              >
                {h.type}
              </span>
            ) : null}
          </div>

          <div className="acctRowMeta">
            {h.meta}
            {h.result ? <span style={{ opacity: 0.75 }}> · {h.result}</span> : null}
          </div>
        </div>
      ))}

      {normalize.length > 0 && (
        <button type="button" onClick={onClear} className="acctDangerLink">
          Clear history
        </button>
      )}
    </section>
  );
}